import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  LegalEntityAddressDto,
  LegalEntityDto,
  LegalEntityRequestDto,
  PaginationRequest,
  ResponseDtoBoolean,
  ResponseDtoLegalEntityAddressDto,
  ResponseDtoLegalEntityDto,
  ResponseDtoLegalEntityMetadataDto,
  ResponseDtoLegalEntityNameAliasDto,
  ResponseDtoListLegalEntityDto,
  ResponseDtoPageLegalEntityDto,
  ResponseDtoString,
  UpsertLegalEntityAliasRequest,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';

// Interface for pagination and search parameters
interface LegalEntityListParams {
  keySearch?: string;
  page: number;
  size: number;
  sortingField?: string;
  sortingDirection?: 'ASC' | 'DESC';
  filters?: LegalEntityFilters;
}

/**
 * Hook to fetch a paginated list of legal entities with optional search
 * @param params Pagination and search parameters
 * @returns Query result with paginated legal entities data
 */
export const useLegalEntityList = (params: LegalEntityListParams): UseQueryResult<ResponseDtoPageLegalEntityDto> =>
  useQuery(
    ['legalEntities', params],
    async () => {
      // Create pagination parameters object
      const paginationRequest: PaginationRequest = {
        page: params.page,
        size: params.size,
      };

      // Add sort parameter if provided
      if (params.sortingField && params.sortingDirection) {
        paginationRequest.sortingColum = params.sortingField;
        paginationRequest.sortingDirection = params.sortingDirection;
      }

      // Create request object with pagination
      const requestDto: LegalEntityRequestDto = {
        paginationRequest,
      };

      // Add filter parameters if provided
      if (params.filters) {
        // Add name filters
        if (params.filters.names?.length) {
          requestDto.names = params.filters.names;
        }

        // Add code filters
        if (params.filters.codes?.length) {
          requestDto.codes = params.filters.codes;
        }

        // Add national currency filters
        if (params.filters.nationalCurrencies?.length) {
          requestDto.nationalCurrencies = params.filters.nationalCurrencies;
        }

        // Add billed entity status filters
        if (params.filters.isBilledEntities?.length) {
          requestDto.isBilledEntities = params.filters.isBilledEntities;
        }

        // Add parent ID filters
        if (params.filters.parentIds?.length) {
          requestDto.parentIds = params.filters.parentIds.map(Number);
        }
      }

      const response = await tFinancialService.api.getAllLegalEntities(requestDto);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

/**
 * Hook to fetch a specific legal entity by ID or code
 * @param id Optional legal entity ID
 * @param code Optional legal entity code
 * @returns Query result with legal entity data
 */
export const useLegalEntityDetail = (id?: number, code?: string): UseQueryResult<ResponseDtoLegalEntityDto> =>
  useQuery(
    ['legalEntity', { id, code }],
    async () => {
      const response = await tFinancialService.api.getLegalEntityByIdOrCode({
        id,
        code,
      });
      return response.data;
    },
    {
      enabled: !!(id || code), // Only run the query if either id or code is provided
    }
  );

/**
 * Hook to create or update legal entities
 * @returns Mutation function for upserting legal entities
 */
export const useUpsertLegalEntity = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (legalEntity: LegalEntityDto | LegalEntityDto[]): Promise<ResponseDtoListLegalEntityDto> => {
      // Convert single entity to array if needed
      const entities = Array.isArray(legalEntity) ? legalEntity : [legalEntity];
      const response = await tFinancialService.api.upsertLegalEntities(entities);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate relevant queries to refetch data
        queryClient.invalidateQueries(['legalEntities']);
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey[0];
            return queryKey === 'legalEntity';
          },
        });
      },
    }
  );
};

/**
 * Hook to create or update a legal entity address
 * @returns Mutation function for upserting a legal entity address
 */
export const useUpsertLegalEntityAddress = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (address: LegalEntityAddressDto): Promise<ResponseDtoLegalEntityAddressDto> => {
      const response = await tFinancialService.api.upsertLegalEntityAddress(address);
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        // Invalidate relevant queries to refetch data
        if (variables.legalEntityId) {
          queryClient.invalidateQueries(['legalEntity', { id: variables.legalEntityId }]);
        }
      },
    }
  );
};

/**
 * Hook to delete legal entity addresses
 * @returns Mutation function for deleting legal entity addresses
 */
export const useDeleteLegalEntityAddresses = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (addressIds: number[]): Promise<ResponseDtoString> => {
      const response = await tFinancialService.api.deleteLegalEntityAddresses(addressIds);
      return response.data;
    },
    {
      onSuccess: () => {
        // Since we don't know which legal entity these addresses belong to,
        // we invalidate all legal entity queries
        queryClient.invalidateQueries(['legalEntity']);
      },
    }
  );
};

/**
 * Hook to create or update a legal entity alias
 * @returns Mutation function for upserting a legal entity alias
 */
export const useUpsertLegalEntityAlias = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (alias: UpsertLegalEntityAliasRequest): Promise<ResponseDtoLegalEntityNameAliasDto> => {
      const response = await tFinancialService.api.upsertLegalEntityAlias(alias);
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        // Invalidate relevant queries to refetch data
        queryClient.invalidateQueries(['legalEntity', { id: variables.legalEntityId }]);
      },
    }
  );
};

/**
 * Hook to delete a legal entity alias
 * @returns Mutation function for deleting a legal entity alias
 */
export const useDeleteLegalEntityAlias = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (aliasId: number): Promise<ResponseDtoBoolean> => {
      const response = await tFinancialService.api.deleteLegalEntityAlias(aliasId);
      return response.data;
    },
    {
      onSuccess: () => {
        // Since we don't know which legal entity this alias belongs to,
        // we invalidate all legal entity queries
        queryClient.invalidateQueries(['legalEntity']);
      },
    }
  );
};

/**
 * Hook to migrate a legal entity to another one (merge legal entities)
 * @returns Mutation function for migrating legal entities
 */
/**
 * Hook to fetch legal entities metadata for filter options
 * This provides filter options for names, codes, currencies, etc.
 * @returns Query result with legal entities metadata
 */
/**
 * Interface for legal entities filter parameters
 */
export interface LegalEntityFilters {
  names?: string[];
  codes?: string[];
  nationalCurrencies?: string[];
  isBilledEntities?: boolean[];
  parentIds?: string[];
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

/**
 * Hook to fetch legal entities metadata for filtering options
 * This provides filter options for names, codes, currencies, etc.
 */
export const useLegalEntitiesMetadata = (filters: LegalEntityFilters = {}): UseQueryResult<ResponseDtoLegalEntityMetadataDto> =>
  useQuery(
    ['legalEntitiesMetadata', filters],
    async (): Promise<ResponseDtoLegalEntityMetadataDto> => {
      // Build request data with filters
      const requestDto: LegalEntityRequestDto = {
        paginationRequest: null, // Will be set below if needed
      };

      // Create pagination request if pagination parameters are provided
      if (filters.page !== undefined || filters.size !== undefined || filters.sortBy || filters.sortDirection) {
        const paginationRequest: PaginationRequest = {};

        if (filters.page !== undefined) paginationRequest.page = filters.page;
        if (filters.size !== undefined) paginationRequest.size = filters.size;
        if (filters.sortBy) paginationRequest.sortingColum = filters.sortBy;
        if (filters.sortDirection) paginationRequest.sortingDirection = filters.sortDirection;

        requestDto.paginationRequest = paginationRequest;
      }

      // Only add filter arrays if they have values
      if (filters.names?.length) requestDto.names = filters.names;
      if (filters.codes?.length) requestDto.codes = filters.codes;
      if (filters.nationalCurrencies?.length) requestDto.nationalCurrencies = filters.nationalCurrencies;
      if (filters.isBilledEntities?.length) requestDto.isBilledEntities = filters.isBilledEntities;
      if (filters.parentIds?.length) requestDto.parentIds = filters.parentIds.map(Number);

      return (await tFinancialService.api.getAllLegalEntitiesMetaData(requestDto)).data;
    },
    {
      // Keep the data fresh but don't refetch on window focus
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    }
  );

export const useMigrateLegalEntity = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ oldLegalEntityId, newLegalEntityId }: { oldLegalEntityId: number; newLegalEntityId: number }): Promise<ResponseDtoString> => {
      const response = await tFinancialService.api.migrateALegalEntityToAnotherOne({
        oldLegalEntityId,
        newLegalEntityId,
      });
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate relevant queries after successful migration
        queryClient.invalidateQueries(['legalEntities']);
        queryClient.invalidateQueries(['legalEntity']);
      },
    }
  );
};
